import React, { useState } from 'react';
import { CreateUserRequest } from '../types';
import userService from '../services/userService';
import { useConfigurationOptions } from '../../configuration/hooks/useConfiguration';
import '../styles/UserManagement.css';

interface AddUserFormProps {
  onUserCreated: () => void;
  onCancel: () => void;
}

export const AddUserForm: React.FC<AddUserFormProps> = ({
  onUserCreated,
  onCancel,
}) => {
  // Function to get default permissions for each role
  const getDefaultPermissions = (role: string): string[] => {
    switch (role) {
      case 'admin':
        return ['settings', 'configuration', 'user-management'];
      case 'manufacturer':
        return ['settings', 'invoices', 'orders', 'product-management'];
      case 'practitioner':
        return ['settings', 'demographics', 'orders', 'insurance'];
      case 'insurance':
        return ['settings', 'insurance'];
      default:
        return ['settings'];
    }
  };

  // Load configuration options
  const { options: roleOptions, isLoading: rolesLoading } =
    useConfigurationOptions('user_roles');
  const { options: genderOptions, isLoading: genderLoading } =
    useConfigurationOptions('gender_options');
  const { options: permissionOptions, isLoading: permissionsLoading } =
    useConfigurationOptions('user_permissions');

  const [formData, setFormData] = useState<CreateUserRequest>({
    email: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'prefer-not-to-say',
    role: 'practitioner',
    permissions: getDefaultPermissions('practitioner'), // Default permissions for practitioner
    notificationPreferences: {
      email: true,
      push: false,
      sms: false,
    },
    phoneNumber: '',
    department: '',
    employeeId: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Function to get available permissions for each role
  const getAvailablePermissions = (
    role: string
  ): { value: string; label: string }[] => {
    // Use configuration data if available, otherwise fallback to hardcoded values
    if (permissionOptions.length > 0) {
      switch (role) {
        case 'admin':
          return permissionOptions.filter(perm =>
            [
              'user-management',
              'settings',
              'configuration',
              'demographics',
              'product-management',
              'orders',
              'invoices',
              'insurance',
            ].includes(perm.value)
          );
        case 'manufacturer':
          return permissionOptions.filter(perm =>
            [
              'settings',
              'demographics',
              'product-management',
              'orders',
              'invoices',
              'insurance',
            ].includes(perm.value)
          );
        case 'practitioner':
          return permissionOptions.filter(perm =>
            [
              'dashboard',
              'demographics',
              'orders',
              'invoices',
              'insurance',
              'settings',
            ].includes(perm.value)
          );
        case 'insurance':
          return permissionOptions.filter(perm =>
            ['insurance', 'settings'].includes(perm.value)
          );
        default:
          return permissionOptions.filter(perm => perm.value === 'settings');
      }
    }

    // Fallback to hardcoded values if configuration is not loaded
    switch (role) {
      case 'admin':
        return [
          { value: 'user-management', label: 'User Management' },
          { value: 'settings', label: 'Settings' },
        ];
      case 'manufacturer':
        return [
          { value: 'dashboard', label: 'Dashboard' },
          { value: 'orders', label: 'Orders' },
          { value: 'invoices', label: 'Invoices' },
          { value: 'settings', label: 'Settings' },
        ];
      case 'practitioner':
        return [
          { value: 'dashboard', label: 'Dashboard' },
          { value: 'demographics', label: 'Demographics' },
          { value: 'orders', label: 'Orders' },
          { value: 'invoices', label: 'Invoices' },
          { value: 'verifications', label: 'Verifications' },
          { value: 'settings', label: 'Settings' },
        ];
      case 'insurance':
        return [
          { value: 'dashboard', label: 'Dashboard' },
          { value: 'insurance', label: 'Insurance' },
          { value: 'verifications', label: 'Verifications' },
          { value: 'settings', label: 'Settings' },
        ];
      default:
        return [{ value: 'settings', label: 'Settings' }];
    }
  };

  // Update permissions when role changes
  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = e.target.value;
    const availablePermissions = getAvailablePermissions(newRole);
    const defaultPermissions = getDefaultPermissions(newRole);

    // Filter default permissions to only include those available for the role
    const validDefaultPermissions = defaultPermissions.filter(permission =>
      availablePermissions.some(ap => ap.value === permission)
    );

    // Always ensure settings is included
    if (!validDefaultPermissions.includes('settings')) {
      validDefaultPermissions.push('settings');
    }

    setFormData(prev => ({
      ...prev,
      role: newRole as any,
      permissions: validDefaultPermissions,
    }));

    // Clear role error
    if (errors.role) {
      setErrors(prev => ({
        ...prev,
        role: '',
      }));
    }
  };

  // Handle permission changes
  const handlePermissionChange = (
    permissionValue: string,
    checked: boolean
  ) => {
    if (checked) {
      // Add permission
      setFormData(prev => ({
        ...prev,
        permissions: [...prev.permissions, permissionValue],
      }));
    } else {
      // Remove permission, but never remove settings
      if (permissionValue === 'settings') {
        return; // Prevent removing settings
      }
      setFormData(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => p !== permissionValue),
      }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    // Handle phone number input filtering
    if (name === 'phoneNumber') {
      // Only allow numbers, spaces, hyphens, parentheses, plus sign, and dots
      const filteredValue = value.replace(/[^0-9\s\-\(\)\+\.]/g, '');

      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...(prev[parent as keyof CreateUserRequest] as any),
            [child]: filteredValue,
          },
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: filteredValue,
        }));
      }
    } else {
      // Handle all other inputs normally
      if (name.includes('.')) {
        // Handle nested address fields
        const [parent, child] = name.split('.');
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...(prev[parent as keyof CreateUserRequest] as any),
            [child]: value,
          },
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value,
        }));
      }
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = (): {
    isValid: boolean;
    errors: Record<string, string>;
  } => {
    const newErrors: Record<string, string> = {};

    // Required fields validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email/User ID is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 18 || age > 100) {
        newErrors.dateOfBirth = 'Age must be between 18 and 100 years';
      }
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (!formData.permissions || formData.permissions.length === 0) {
      newErrors.permissions = 'At least one permission is required';
    }

    // Phone number validation (optional but validate format if provided)
    if (formData.phoneNumber && formData.phoneNumber.trim()) {
      // Remove all formatting characters for validation
      const cleanPhone = formData.phoneNumber.replace(/[\s\-\(\)\+\.]/g, '');

      // Check if it contains only digits
      if (!/^\d+$/.test(cleanPhone)) {
        newErrors.phoneNumber =
          'Phone number can only contain numbers and formatting characters (+, -, (), spaces)';
      } else if (cleanPhone.length < 10 || cleanPhone.length > 15) {
        newErrors.phoneNumber = 'Phone number must be between 10 and 15 digits';
      } else {
        // Additional validation for proper international format
        const phoneRegex = /^[\+]?[\d\s\-\(\)\.]{10,20}$/;
        if (!phoneRegex.test(formData.phoneNumber.trim())) {
          newErrors.phoneNumber = 'Please enter a valid phone number format';
        }
      }
    }
    if (formData.address?.zipCode && formData.address.zipCode.trim()) {
      const zipRegex = /^\d+$/; // only digits

      if (!zipRegex.test(formData.address.zipCode)) {
        newErrors['address.zipCode'] =
          'ZIP/Postal code must contain only numbers';
      } else if (
        formData.address.zipCode.length < 4 ||
        formData.address.zipCode.length > 10
      ) {
        newErrors['address.zipCode'] =
          'ZIP/Postal code must be between 4 and 10 digits';
      }
    }

    setErrors(newErrors);
    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateForm();
    if (!validation.isValid) {
      // Scroll to first invalid field using the fresh validation errors
      const firstErrorField = Object.keys(validation.errors).find(
        key => validation.errors[key]
      );
      if (firstErrorField) {
        // Add a small delay to ensure DOM updates are complete
        setTimeout(() => {
          let element = document.getElementById(firstErrorField);

          // Special handling for permissions field which doesn't have a direct input ID
          if (!element && firstErrorField === 'permissions') {
            // Find the permissions section by looking for the permissions-group class
            element = document.querySelector('.permissions-group');
          }

          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });

            // Only focus if it's a focusable element
            if (
              element.tagName === 'INPUT' ||
              element.tagName === 'SELECT' ||
              element.tagName === 'TEXTAREA'
            ) {
              element.focus();
            }
          }
        }, 100);
      }
      return;
    }

    setIsSubmitting(true);

    try {
      await userService.createUser(formData);
      onUserCreated();
    } catch (error) {
      console.error('Error creating user:', error);
      // Handle specific error messages
      if (error instanceof Error) {
        if (error.message.includes('Email already exists')) {
          setErrors(prev => ({ ...prev, email: 'Email already exists' }));
        } else {
          alert(`Failed to create user: ${error.message}`);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-user-form-overlay">
      <div className="add-user-form-container">
        <div className="form-header">
          <h2>Add New User</h2>
          <button
            type="button"
            className="close-button"
            onClick={onCancel}
            aria-label="Close form"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="add-user-form">
          <div className="form-section">
            <h3>Basic Information</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email/User ID *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="Enter email address (will be used as User ID)"
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={errors.firstName ? 'error' : ''}
                  placeholder="Enter first name"
                  disabled={isSubmitting}
                />
                {errors.firstName && (
                  <span className="error-message">{errors.firstName}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={errors.lastName ? 'error' : ''}
                  placeholder="Enter last name"
                  disabled={isSubmitting}
                />
                {errors.lastName && (
                  <span className="error-message">{errors.lastName}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dateOfBirth">Date of Birth *</label>
                <input
                  type="date"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  className={errors.dateOfBirth ? 'error' : ''}
                  disabled={isSubmitting}
                />
                {errors.dateOfBirth && (
                  <span className="error-message">{errors.dateOfBirth}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="gender">Gender *</label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className={errors.gender ? 'error' : ''}
                  disabled={isSubmitting || genderLoading}
                >
                  {genderLoading ? (
                    <option value="">Loading gender options...</option>
                  ) : (
                    genderOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))
                  )}
                </select>
                {errors.gender && (
                  <span className="error-message">{errors.gender}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="role">Role *</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleRoleChange}
                  className={errors.role ? 'error' : ''}
                  disabled={isSubmitting || rolesLoading}
                >
                  {rolesLoading ? (
                    <option value="">Loading roles...</option>
                  ) : (
                    roleOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))
                  )}
                </select>
                {errors.role && (
                  <span className="error-message">{errors.role}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="e.g., +1-555-123-4567 or (555) 123-4567"
                  pattern="[+]?[\d\s\-\(\)\.]{10,20}"
                  title="Please enter a valid phone number with 10-15 digits. You can use formatting like +1-555-123-4567 or (555) 123-4567"
                  className={errors.phoneNumber ? 'error' : ''}
                  disabled={isSubmitting}
                />
                {errors.phoneNumber && (
                  <span className="error-message">{errors.phoneNumber}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group permissions-group">
                <label>Permissions *</label>
                <div className="permissions-options">
                  {permissionsLoading ? (
                    <div className="loading-permissions">
                      Loading permissions...
                    </div>
                  ) : (
                    getAvailablePermissions(formData.role).map(permission => (
                      <div
                        key={permission.value}
                        className="permission-option"
                      >
                        <input
                          type="checkbox"
                          id={`permission-${permission.value}`}
                          checked={formData.permissions.includes(
                            permission.value
                          )}
                          onChange={e =>
                            handlePermissionChange(
                              permission.value,
                              e.target.checked
                            )
                          }
                          disabled={
                            isSubmitting || permission.value === 'settings'
                          }
                        />
                        <label htmlFor={`permission-${permission.value}`}>
                          {permission.label}
                          {permission.value === 'settings' && (
                            <span className="permission-note">
                              {' '}(Always enabled)
                            </span>
                          )}
                        </label>
                      </div>
                    ))
                  )}
                </div>
                <p className="setting-description">
                  Select the modules this user can access. Settings is always
                  enabled by default.
                </p>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Notification Preferences</h3>
            <p className="section-description">
              Configure how this user will receive notifications. These settings
              cannot be changed by the user.
            </p>

            <div className="form-row">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.notificationPreferences.email}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        notificationPreferences: {
                          ...prev.notificationPreferences,
                          email: e.target.checked,
                        },
                      }))
                    }
                    disabled={isSubmitting}
                  />
                  <span className="checkbox-text">Email Notifications</span>
                </label>
                <p className="setting-description">
                  Receive important updates via email
                </p>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Professional Information</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="department">Department</label>
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  placeholder="Enter department"
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="employeeId">Employee ID</label>
                <input
                  type="text"
                  id="employeeId"
                  name="employeeId"
                  value={formData.employeeId}
                  onChange={handleInputChange}
                  placeholder="Enter employee ID"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Address Information</h3>

            <div className="form-group">
              <label htmlFor="address.street">Street Address</label>
              <input
                type="text"
                id="address.street"
                name="address.street"
                value={formData.address?.street || ''}
                onChange={handleInputChange}
                placeholder="Enter street address"
                disabled={isSubmitting}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="address.city">City</label>
                <input
                  type="text"
                  id="address.city"
                  name="address.city"
                  value={formData.address?.city || ''}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="address.state">State/Province</label>
                <input
                  type="text"
                  id="address.state"
                  name="address.state"
                  value={formData.address?.state || ''}
                  onChange={handleInputChange}
                  placeholder="Enter state/province"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="address.zipCode">ZIP/Postal Code</label>
                <input
                  type="number"
                  id="address.zipCode"
                  name="address.zipCode"
                  value={formData.address?.zipCode || ''}
                  onChange={handleInputChange}
                  placeholder="Enter ZIP/postal code"
                  disabled={isSubmitting}
                  inputMode="numeric" // mobile keyboards show numbers
                  pattern="\d*" // only digits
                  maxLength={10} // optional: restrict max length
                  className={errors['address.zipCode'] ? 'error' : ''}
                />
                {errors['address.zipCode'] && (
                  <span className="error-message">
                    {errors['address.zipCode']}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="address.country">Country</label>
                <input
                  type="text"
                  id="address.country"
                  name="address.country"
                  value={formData.address?.country || ''}
                  onChange={handleInputChange}
                  placeholder="Enter country"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Additional Information</h3>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Enter any additional notes about the user"
                rows={3}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-primary btn-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating User...' : 'Create User'}
            </button>
          </div>

          <div className="form-info">
            <p>
              <strong>Note:</strong> A temporary password will be automatically
              generated and sent to the user's email address. The password will
              expire in 7 days.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
